/* eslint-disable arrow-body-style */
import {AbstractStore} from "./abstract-store";
import {action, autorun, computed, makeObservable, observable} from "mobx";
import {ApiLayerConfiguration} from "../config/auftragsverwaltung-api-configuration";
import {JsonSerializer, throwError} from "typescript-json-serializer";
import {MapState} from "../core/map-state";
import EsriFeatureLayer from "@arcgis/core/layers/FeatureLayer";
import {Parcel, ParcelProps} from "../models/parcel";

export interface ParcelStoreProps {
    layerConfig: ApiLayerConfiguration;
    mapStore: MapState;
}

export class ParcelStore extends AbstractStore {
    mapStore: MapState;

    /**
     * Loaded items (local store)
     */
    items = observable.map<string, Parcel>();

    /**
     * The representation of the API layer, but loaded in the map view
     */
    viewLayer: EsriFeatureLayer;

    /**
     * The layer view of the API Layer in the map view
     * @param props
     */
    viewLayerView: __esri.FeatureLayerView;

    constructor(props: ParcelStoreProps) {

        super({ layerConfig: props.layerConfig });

        this.mapStore = props.mapStore;

        makeObservable(this, {
            status: observable,
            dataStatus: observable,

            items: observable,
            selectedItem: observable,
            setSelectedItem: action,
            resetSelectedItem: action,
            visibleItems: computed,

            addItem: action,
            removeItem: action,

            isDataLoading: computed,
            isDataLoaded: computed,
            isDataError: computed,
            isLoading: computed,
            isLoaded: computed,
            isNotFound: computed,
            isError: computed
        });
    }

    get visibleItems(): Parcel[] {
        // apply filters
        const filtered = [...this.items.values()].filter(() => {
            return true;
        });
        // sort
        // filtered.sort(Parcel.sortByName);
        return [ ...filtered];
    }

    /**
     * Override to use egrid as key
     * Add one or multiple items to the store
     * @param item One item or a map with multiple items with GUID as key
     */
    addItem(item: Parcel | Map<string, Parcel>): void {
        if (item instanceof Map) {
            // multiple items
            this.items.merge(item);
        }
        else {
            // one item
            if (this.items.has(String(item.egrid))) {
                this.items.delete(String(item.egrid));
            }
            this.items.set(String(item.egrid), item);
        }
    }

    /**
     * Override to use egrid as key
     * Remove an item from store
     * @param item
     */
    removeItem(item: Parcel): void {
        if (this.items.has(String(item.egrid))) {
            this.items.delete(String(item.egrid));
        }
    }

    /**
     * Override to use egrid as key
     * Get an item by GUID
     * @param bfsNr
     */
    getItem(egrid: string): Parcel | null {
        const item = this.items.get(egrid);
        return item || null;
    }

    /**
     * Request all parcels from server and store in local store
     *
     * @param egrids
     */
    loadItems(egrids?: string[]): Promise<Parcel[]> {
        return new Promise((resolve, reject) => {
            this.setDataStatus("loading");

            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                this.queryFeatures(egrids).then((parcels: Parcel[]) => {
                    if (parcels) {
                        this.addParcels(parcels);
                        this.setDataStatus("loaded");

                        resolve(parcels);
                    } else {
                        this.setDataStatus("error");
                        reject();
                        throw new Error("Error requesting perimeters");
                    }
                });
            });
        });
    }

    /**
     * Create a new item with a feature
     * Store item to local store and select
     * @param user
     */
    newItem(user: string): Promise<Parcel | null> {
        return new Promise((resolve) => {
            const perimeter = new Parcel({
                creator: user,
                guid: crypto.randomUUID()
            });

            // create feature
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                // TODO: find out why the feature template prototype does not return attributes
                perimeter.feature = this.createFeature(null, perimeter);

                // workaround: add missing prototype attributes
                Parcel.prototypeAttributes.forEach((attrName) => {
                    if (!perimeter.feature.attributes[attrName]) {
                        perimeter.feature.attributes[attrName] = null;
                    }
                });

                this.addItem(perimeter);
                this.setSelectedItem(perimeter);

                resolve(perimeter);
            });
        });
    }

    addParcels(parcels: Parcel[] | [] | null): void {
        // do only save to mobx when not requested with specified ids or contacts
        const map = new Map<string, Parcel>();
        parcels?.forEach((a: Parcel) => {
            map.set(a.egrid, a);
        });
        this.addItem(map);
    }

    queryFeatures(egrids?: string[],
                  geometry?: __esri.Geometry,
                  spatialRelationship?: "intersects"
                      | "contains"
                      | "crosses"
                      | "disjoint"
                      | "envelope-intersects"
                      | "index-intersects"
                      | "overlaps"
                      | "touches"
                      | "within"
                      | "relation"): Promise<Parcel[] | [] | null> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose();

                // Get a query object for the layer's current configuration
                // queryParams.outFields will be set to ["*"] to get values
                // for all available fields.
                const queryParams = this.layer.createQuery();
                if (geometry) {
                    queryParams.geometry = geometry;
                }
                if (spatialRelationship) {
                    queryParams.spatialRelationship = spatialRelationship;
                }
                if (egrids !== undefined) {
                    queryParams.where += ` AND egris_egrid IN ('${egrids.join("','")}')`;
                }

                // query the layer with the modified params object
                this.layer.queryFeatures(queryParams).then((results: __esri.FeatureSet) => {
                    if (results.features) {
                        const activities = this.parseQueryResult(results);
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        resolve(activities);
                    } else {
                        reject();
                    }
                }, (error) => {
                    throw new Error(`Error querying parcels + ${error}`);
                });
            });
        });
    }

    /**
     * Override loadLayer to also load view layer
     * @param layerConfig
     * @protected
     */
    protected loadLayer(layerConfig: ApiLayerConfiguration): Promise<EsriFeatureLayer> {
        return new Promise((resolve, reject) => {
            try {
                // wait for map view > find the instance of this layer loaded to the map
                // the parcel layer is loaded twice. Once for the API, once to show in the map
                autorun((reaction: any) => {
                    if (!this.mapStore?.map) {
                        return;
                    }
                    reaction.dispose(); // stop autorun

                    // load is returning already loaded promise when calling multiple times
                    this.viewLayer = this.mapStore.map.layers.find((layer: __esri.FeatureLayer) => {
                        return layer.portalItem?.id === this.layer.portalItem.id &&
                            layer.layerId === this.layer.layerId;
                    }) as __esri.FeatureLayer;

                    if (this.viewLayer) {
                        // do not show in layer list
                        this.viewLayer.listMode = "hide";

                        // also store view of this layer to access it later more easily
                        this.mapStore.view?.whenLayerView(this.viewLayer).then((layerView) => {
                            this.viewLayerView = layerView;
                        });
                    }
                });

                const featureLayer: EsriFeatureLayer =
                    new EsriFeatureLayer(layerConfig as __esri.FeatureLayerProperties);

                featureLayer.load().then(() => {
                    this.layer = featureLayer;
                    resolve(featureLayer);
                }, () => {
                    reject(layerConfig.objectType);
                });
            } catch (errors) {
                console.error(errors);
                reject(layerConfig.objectType);
            }
        });
    }

    private parseQueryResult(data: __esri.FeatureSet): Parcel[] {
        const serializer = new JsonSerializer({
            errorCallback: throwError
        });

        const perimeters: Parcel[] = [];
        data.features?.forEach((feature: any) => {
            const json = serializer.deserialize(feature.attributes, Parcel);
            const a = new Parcel(json as ParcelProps);
            a.feature = feature;
            perimeters.push(a);
        });
        return perimeters;
    }

}
